import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../auth";
import { listTasks, completeTask, createTask } from "../db/tasks";
import { listPlants } from "../db/plants";
import { listLocations } from "../db/locations";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import type {
  TaskRow,
  TaskCategory,
  TaskStatus,
  PlantRow,
  LocationRow,
} from "@garden/types/db";
import "./Tasks.css";

const CATEGORIES: { value: TaskCategory; label: string; color: string }[] = [
  { value: "care", label: "Care", color: "var(--moss)" },
  { value: "growing", label: "Growing", color: "var(--terracotta)" },
  { value: "project", label: "Project", color: "var(--honey)" },
  { value: "purchase", label: "Purchase", color: "var(--rose-faded)" },
  { value: "maintenance", label: "Maintenance", color: "var(--clay)" },
];

const STATUS_OPTIONS: { value: TaskStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "complete", label: "Complete" },
  { value: "skipped", label: "Skipped" },
  { value: "cancelled", label: "Cancelled" },
];

interface Section {
  key: string;
  title: string;
  meta: string;
  variant?: "overdue";
  collapsible?: boolean;
  tasks: TaskRow[];
}

export function Tasks() {
  const [tasks, setTasks] = useState<TaskRow[] | null>(null);
  const [plants, setPlants] = useState<PlantRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<"all" | TaskCategory>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "all">("pending");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    next3: true,
    later: true,
  });
  const [showForm, setShowForm] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const gardenId = useCurrentGardenId();

  const reload = async () => {
    if (!supabase || !gardenId) return;
    try {
      const [t, p, l] = await Promise.all([
        listTasks(supabase, gardenId),
        listPlants(supabase, gardenId),
        listLocations(supabase, gardenId),
      ]);
      setTasks(t);
      setPlants(p);
      setLocations(l);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void reload();
  }, [gardenId]);

  const plantById = useMemo(() => {
    const m = new Map<string, PlantRow>();
    plants.forEach((p) => m.set(p.id, p));
    return m;
  }, [plants]);

  const locationById = useMemo(() => {
    const m = new Map<string, LocationRow>();
    locations.forEach((l) => m.set(l.id, l));
    return m;
  }, [locations]);

  const filtered = useMemo(() => {
    if (!tasks) return [];
    const q = search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (locationFilter !== "all") {
        const taskLocId = t.location_id ?? plantById.get(t.plant_id ?? "")?.location_id ?? null;
        if (taskLocId !== locationFilter) return false;
      }
      if (q) {
        const plant = t.plant_id ? plantById.get(t.plant_id) : null;
        const haystack = [
          t.name,
          t.description ?? "",
          t.notes ?? "",
          plant?.common_name ?? "",
          plant?.scientific_name ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, search, categoryFilter, locationFilter, statusFilter, plantById]);

  const sections: Section[] = useMemo(() => buildSections(filtered), [filtered]);

  const onComplete = async (taskId: string) => {
    if (!supabase) return;
    setWorking(taskId);
    try {
      await completeTask(supabase, { task_id: taskId });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWorking(null);
    }
  };

  const headerMeta = useMemo(() => {
    if (!tasks) return null;
    const open = tasks.filter((t) => t.status === "pending");
    const overdue = open.filter((t) => t.due_date && t.due_date < todayIso()).length;
    const completedThisYear = tasks.filter(
      (t) =>
        t.status === "complete" &&
        t.updated_at.slice(0, 4) === new Date().getFullYear().toString(),
    ).length;
    return `${open.length} open · ${overdue} overdue · ${completedThisYear} completed this year`;
  }, [tasks]);

  return (
    <div className="Tasks">
      <header className="Tasks__head">
        <div>
          <h1>Tasks</h1>
          {headerMeta && <div className="Tasks__meta">{headerMeta}</div>}
        </div>
        <div className="Tasks__headRight">
          <div className="Tasks__search">
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <circle cx="6" cy="6" r="4" />
              <path d="M9 9l3 3" />
            </svg>
            <input
              type="search"
              placeholder="Search tasks"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="Tasks__new"
            onClick={() => setShowForm((s) => !s)}
          >
            + New task
          </button>
        </div>
      </header>

      <div className="Tasks__filters">
        <button
          type="button"
          className={`Tasks__filter ${categoryFilter === "all" ? "Tasks__filter--active" : ""}`}
          onClick={() => setCategoryFilter("all")}
        >
          All categories
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            type="button"
            className={`Tasks__filter ${categoryFilter === c.value ? "Tasks__filter--active" : ""}`}
            onClick={() => setCategoryFilter(c.value)}
          >
            <span className="Tasks__catDot" style={{ background: c.color }} />
            {c.label}
          </button>
        ))}
        <select
          className="Tasks__select"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
        >
          <option value="all">All locations</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
        <select
          className="Tasks__select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TaskStatus | "all")}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {showForm && (
        <NewTaskInline
          plants={plants}
          locations={locations}
          onCreated={() => {
            setShowForm(false);
            void reload();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {error && <p className="Tasks__error">{error}</p>}

      {tasks === null ? (
        <p className="Tasks__loading">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="Tasks__empty">
          No tasks match the current filters. {tasks.length === 0 ? (
            <>You haven't created any tasks yet — try the <strong>+ New task</strong> button above.</>
          ) : (
            "Try clearing search or relaxing a filter."
          )}
        </p>
      ) : (
        sections.map((section) => {
          if (section.tasks.length === 0) return null;
          const isCollapsed = section.collapsible && collapsed[section.key];
          return (
            <section
              key={section.key}
              className={`Tasks__section ${section.variant === "overdue" ? "Tasks__section--overdue" : ""}`}
            >
              <header
                className={`Tasks__sectionH ${section.collapsible ? "Tasks__sectionH--clickable" : ""}`}
                onClick={() =>
                  section.collapsible &&
                  setCollapsed((c) => ({ ...c, [section.key]: !c[section.key] }))
                }
              >
                <span className="Tasks__sectionTitle">{section.title}</span>
                <span className="Tasks__sectionMeta">
                  {section.meta} · {section.tasks.length} task{section.tasks.length === 1 ? "" : "s"}
                  {section.collapsible ? (isCollapsed ? " · collapsed" : "") : ""}
                </span>
                {section.collapsible && (
                  <span className="Tasks__sectionToggle">{isCollapsed ? "▸" : "▾"}</span>
                )}
              </header>
              {!isCollapsed &&
                section.tasks.map((t) => (
                  <TaskRowView
                    key={t.id}
                    task={t}
                    plant={t.plant_id ? plantById.get(t.plant_id) ?? null : null}
                    location={(() => {
                      const id =
                        t.location_id ??
                        (t.plant_id ? plantById.get(t.plant_id)?.location_id ?? null : null);
                      return id ? locationById.get(id) ?? null : null;
                    })()}
                    busy={working === t.id}
                    onComplete={onComplete}
                  />
                ))}
            </section>
          );
        })
      )}
    </div>
  );
}

function TaskRowView({
  task,
  plant,
  location,
  busy,
  onComplete,
}: {
  task: TaskRow;
  plant: PlantRow | null;
  location: LocationRow | null;
  busy: boolean;
  onComplete: (id: string) => void;
}) {
  const cat = CATEGORIES.find((c) => c.value === task.category);
  const overdue = task.status === "pending" && task.due_date && task.due_date < todayIso();
  const isPending = task.status === "pending";
  return (
    <div className={`Tasks__row ${task.status !== "pending" ? "Tasks__row--done" : ""}`}>
      <button
        type="button"
        className={`Tasks__tick ${task.status !== "pending" ? "Tasks__tick--done" : ""}`}
        disabled={!isPending || busy}
        aria-label={isPending ? `Mark "${task.name}" complete` : "Already complete"}
        onClick={() => isPending && onComplete(task.id)}
      />
      <div className={`Tasks__date ${overdue ? "Tasks__date--overdue" : ""}`}>
        {task.due_date ? formatShortDate(task.due_date) : "—"}
        {overdue && <div className="Tasks__dateAgo">{daysAgo(task.due_date!)} ago</div>}
      </div>
      <div className="Tasks__nameCol">
        <div className="Tasks__name">{task.name}</div>
        {plant && (
          <Link to={`/plants/${plant.id}`} className="Tasks__plant">
            {plant.scientific_name ?? plant.common_name}
            {plant.cultivar ? ` '${plant.cultivar}'` : ""}
          </Link>
        )}
      </div>
      <div className="Tasks__cat">
        <span className="Tasks__catDot" style={{ background: cat?.color ?? "var(--ink-faint)" }} />
        {cat?.label ?? task.category}
      </div>
      <div className="Tasks__loc">{location?.name ?? "—"}</div>
    </div>
  );
}

function NewTaskInline({
  plants,
  locations,
  onCreated,
  onCancel,
}: {
  plants: PlantRow[];
  locations: LocationRow[];
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<TaskCategory>("care");
  const [dueDate, setDueDate] = useState("");
  const [plantId, setPlantId] = useState<string>("");
  const [locationId, setLocationId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    if (name.trim() === "") return;
    setSaving(true);
    setError(null);
    try {
      await createTask(supabase, {
        name: name.trim(),
        category,
        due_date: dueDate === "" ? null : dueDate,
        plant_id: plantId === "" ? null : plantId,
        location_id: locationId === "" ? null : locationId,
        description: description.trim() === "" ? null : description,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <form className="Tasks__newForm" onSubmit={onSubmit}>
      <div className="Tasks__newRow">
        <label className="Tasks__newField Tasks__newField--wide">
          <span>Task name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sow runner beans indoors"
            required
            maxLength={200}
            autoFocus
          />
        </label>
        <label className="Tasks__newField">
          <span>Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as TaskCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="Tasks__newField">
          <span>Due date</span>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
      </div>
      <div className="Tasks__newRow">
        <label className="Tasks__newField">
          <span>Plant (optional)</span>
          <select
            value={plantId}
            onChange={(e) => {
              setPlantId(e.target.value);
              setLocationId("");
            }}
          >
            <option value="">—</option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.common_name}
                {p.cultivar ? ` '${p.cultivar}'` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="Tasks__newField">
          <span>Location (optional)</span>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={plantId !== ""}
            title={plantId !== "" ? "Cleared because the plant carries its own location" : undefined}
          >
            <option value="">—</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>
        <label className="Tasks__newField Tasks__newField--wide">
          <span>Description (optional)</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Anything you want to remember"
          />
        </label>
      </div>
      {error && <p className="Tasks__error">{error}</p>}
      <div className="Tasks__newActions">
        <button type="button" className="Tasks__newBtn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="submit"
          className="Tasks__newBtn Tasks__newBtn--primary"
          disabled={saving || name.trim() === ""}
        >
          {saving ? "Saving…" : "Add task"}
        </button>
      </div>
    </form>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

function daysAgo(iso: string): string {
  const d = new Date(iso + "T00:00:00").getTime();
  const now = Date.now();
  const days = Math.max(0, Math.floor((now - d) / 86400000));
  return days === 0 ? "today" : `${days}d`;
}

function buildSections(tasks: TaskRow[]): Section[] {
  const today = new Date();
  const todayStr = todayIso();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const next3End = new Date(today.getFullYear(), today.getMonth() + 4, 0);
  const yearEnd = new Date(today.getFullYear(), 11, 31);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const overdue: TaskRow[] = [];
  const thisMonth: TaskRow[] = [];
  const next3: TaskRow[] = [];
  const later: TaskRow[] = [];
  const undated: TaskRow[] = [];
  const beyond: TaskRow[] = [];

  for (const t of tasks) {
    if (!t.due_date) {
      undated.push(t);
      continue;
    }
    if (t.status === "pending" && t.due_date < todayStr) {
      overdue.push(t);
    } else if (t.due_date >= fmt(monthStart) && t.due_date <= fmt(monthEnd)) {
      thisMonth.push(t);
    } else if (t.due_date > fmt(monthEnd) && t.due_date <= fmt(next3End)) {
      next3.push(t);
    } else if (t.due_date > fmt(next3End) && t.due_date <= fmt(yearEnd)) {
      later.push(t);
    } else {
      beyond.push(t);
    }
  }

  // Within each section, sort by due_date asc
  const byDue = (a: TaskRow, b: TaskRow) =>
    (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999");
  overdue.sort(byDue);
  thisMonth.sort(byDue);
  next3.sort(byDue);
  later.sort(byDue);
  beyond.sort(byDue);

  const monthLabel = today.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const next3Label = labelMonthRange(today.getMonth() + 1, today.getMonth() + 3, today.getFullYear());

  return [
    {
      key: "overdue",
      title: "Overdue",
      meta: overdue.length === 0
        ? ""
        : `oldest ${daysAgo(overdue[0].due_date ?? todayStr)} ago`,
      variant: "overdue",
      tasks: overdue,
    },
    {
      key: "thisMonth",
      title: monthLabel,
      meta: "This month",
      tasks: thisMonth,
    },
    {
      key: "next3",
      title: next3Label,
      meta: "Next 3 months",
      collapsible: true,
      tasks: next3,
    },
    {
      key: "later",
      title: `Later in ${today.getFullYear()}`,
      meta: "Later this year",
      collapsible: true,
      tasks: later,
    },
    {
      key: "beyond",
      title: "Beyond this year",
      meta: "Future",
      collapsible: true,
      tasks: beyond,
    },
    {
      key: "undated",
      title: "No due date",
      meta: "Undated",
      tasks: undated,
    },
  ];
}

function labelMonthRange(startIdx: number, endIdx: number, year: number): string {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const start = ((startIdx % 12) + 12) % 12;
  const end = ((endIdx % 12) + 12) % 12;
  const startYear = startIdx >= 12 ? year + 1 : year;
  const endYear = endIdx >= 12 ? year + 1 : year;
  if (startYear === endYear) {
    return `${months[start]} – ${months[end]} ${startYear}`;
  }
  return `${months[start]} ${startYear} – ${months[end]} ${endYear}`;
}
